/**
 * Traduce el `errorCode` estructurado que devuelve el backend en el job de
 * análisis (GET /nutrition-analyses/:id) a un mensaje claro para el usuario.
 *
 * Filosofía (app de salud): mensajes honestos y calmados, nunca alarmistas, y
 * siempre con una acción siguiente clara (reintentar / rellenar a mano). Ante un
 * código desconocido caemos al mensaje del backend si es legible, o a uno genérico.
 *
 * Los códigos provienen del orchestrator (`contracts/error-codes.ts`) y del
 * backend (`ANALYSIS_STALE`, `ENQUEUE_FAILED`, `MAX_RETRIES`).
 */

const MESSAGES: Record<string, string> = {
  // Imagen que no es comida (guardián no-comida, P1-2).
  NON_FOOD_IMAGE:
    'La foto no parece un plato de comida. Prueba con una imagen del plato bien encuadrado y con buena luz.',
  // Job zombie cerrado por timeout (P0-2).
  ANALYSIS_STALE:
    'El análisis tardó demasiado y se canceló. Vuelve a intentarlo; si persiste, introduce los datos a mano.',
  // No se pudo encolar el job (Pub/Sub caído).
  ENQUEUE_FAILED:
    'No pudimos iniciar el análisis. Revisa tu conexión e inténtalo de nuevo.',
  // Mensaje envenenado descartado tras varios reintentos.
  MAX_RETRIES:
    'El análisis falló tras varios intentos. Puedes reintentar o introducir los datos a mano.',
  // Ningún proveedor produjo un resultado aceptable.
  ALL_PROVIDERS_FAILED:
    'El servicio de análisis no está disponible ahora mismo. Inténtalo en un momento o rellena a mano.',
  // Protecciones de coste (tope de presupuesto / circuit-breaker). Mensaje NEUTRO:
  // el paciente NUNCA debe ver detalles de coste/límite de IA — eso es solo del admin.
  COST_BUDGET_EXCEEDED:
    'El análisis no está disponible en este momento. Inténtalo más tarde o introduce los datos a mano.',
  CIRCUIT_BREAKER_OPEN:
    'El análisis no está disponible en este momento. Inténtalo más tarde o introduce los datos a mano.',
};

/** Códigos del orchestrator que comparten el mismo mensaje de "servicio no disponible". */
const PROVIDER_UNAVAILABLE_PREFIX = 'PROVIDER_';

// Términos que NO deben llegar al paciente en un mensaje (solo el admin ve el
// coste de IA). Si el texto crudo del backend los contiene → mensaje genérico.
const COST_TERMS = /coste|costo|cost|presupuesto|budget|l[íi]mite|limit|cuota|quota|gasto|\busd\b|d[óo]lar|\$/i;

const GENERIC =
  'No se pudo analizar la foto. Puedes reintentar o introducir los datos a mano.';

/**
 * Devuelve el mensaje amable para un job fallido.
 * @param errorCode código estructurado del backend (puede faltar en fallos antiguos).
 * @param fallbackMessage mensaje crudo del backend, usado solo si el código es desconocido y el texto es legible.
 */
export function analysisErrorMessage(
  errorCode?: string,
  fallbackMessage?: string,
): string {
  if (errorCode) {
    if (MESSAGES[errorCode]) return MESSAGES[errorCode];
    if (errorCode.startsWith(PROVIDER_UNAVAILABLE_PREFIX)) {
      return MESSAGES.ALL_PROVIDERS_FAILED;
    }
  }
  // Sin código conocido: usar el texto del backend si parece un mensaje para
  // humanos (no un identificador tipo SNAKE_CASE) y no está vacío.
  const trimmed = fallbackMessage?.trim();
  if (trimmed && /\s/.test(trimmed) && trimmed.toUpperCase() !== trimmed) {
    // Nunca filtrar detalles de coste/presupuesto al paciente.
    if (COST_TERMS.test(trimmed)) return GENERIC;
    return trimmed;
  }
  return GENERIC;
}
